/* eslint-disable react-refresh/only-export-components */
/**
 * Authentication context for admin dashboard.
 * 
 * Provides authentication state, admin verification, and auth methods
 * to all components in the application.
 * 
 * @module context/AuthContext
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  type ReactNode,
} from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { logAuthEvent, AuditActionTypes } from '../lib/audit';
import type { AdminUser } from '../types';

/**
 * Authentication context type definition.
 */
interface AuthContextType {
  /** Supabase auth user */
  user: User | null;
  /** Admin user record from admin_users table */
  adminUser: AdminUser | null;
  /** Current session */
  session: Session | null;
  /** Whether auth state is being loaded */
  isLoading: boolean;
  /** Whether the user is a verified admin */
  isAdmin: boolean;
  /** Sign in with email and password */
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  /** Sign out and clear session */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Authentication provider component.
 * Wraps the application and provides auth state to all children.
 * 
 * @param props - Provider props with children
 * @returns Provider component
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const adminUserRef = useRef<AdminUser | null>(null);
  // Track current user ID to detect re-authentication vs new user
  const currentUserIdRef = useRef<string | null>(null);

  /**
   * Logs authentication events without blocking user flows.
   * 
   * @param adminUserId - Admin user ID or null for failed logins
   * @param adminEmail - Admin email address
   * @param actionType - Authentication action type
   * @param details - Optional metadata for the audit log
   * @returns Promise that resolves after the log attempt
   * 
   * @security Audit logging failures must never block auth flows.
   */
  const safeLogAuthEvent = useCallback(
    async (
      adminUserId: string | null,
      adminEmail: string,
      actionType:
        | typeof AuditActionTypes.LOGIN
        | typeof AuditActionTypes.LOGOUT
        | typeof AuditActionTypes.LOGIN_FAILED,
      details?: Record<string, unknown>
    ): Promise<void> => {
      try {
        await logAuthEvent(adminUserId, adminEmail, actionType, details);
      } catch (error) {
        console.warn('Auth audit log failed:', error);
      }
    },
    []
  );

  /**
   * Verifies if the authenticated user is an active admin.
   * 
   * SECURITY: This function only reads from admin_users (read-only).
   * last_login_at updates have been removed per CSDEV-138 to comply with
   * read-only RLS policies. Login events are tracked via admin_audit_logs instead.
   * 
   * @see CSDEV-138 - Remove admin_users last_login update
   */
  const verifyAdminUser = useCallback(async (authUserId: string) => {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('auth_user_id', authUserId)
        .eq('is_active', true)
        .single();

      const admin = data as AdminUser | null;

      if (error || !admin) {
        console.warn('User is not an admin or admin verification failed');
        setAdminUser(null);
        return null;
      }

      setAdminUser(admin);
      return admin;
    } catch (err) {
      console.error('Admin verification error:', err);
      setAdminUser(null);
      return null;
    }
  }, []);

  /**
   * Initialize auth state on mount.
   */
  useEffect(() => {
    let isMounted = true;

    // Get initial session
    const initializeSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          await verifyAdminUser(session.user.id);
        }
      } catch (error) {
        console.error('Failed to initialize auth session:', error);
        if (!isMounted) return;
        setSession(null);
        setUser(null);
        setAdminUser(null);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void initializeSession();

    // Listen for auth changes
    // CRITICAL: Synchronous callback to prevent deadlocks during token refresh
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;

      console.log('[AUTH EVENT]', event, {
        userId: session?.user?.id,
        currentUserId: currentUserIdRef.current,
      });

      // Handle different auth events
      if (event === 'SIGNED_IN' && session?.user) {
        // Check if same user re-authenticating (password change, token refresh)
        if (currentUserIdRef.current === session.user.id) {
          console.log('[AUTH] Same user re-authenticating, skipping full state update');
          setSession(session);
          // Don't refetch admin profile for same user
          return;
        }

        // New user - update state immediately (synchronous)
        console.log('[AUTH] New user signed in, full state update');
        setSession(session);
        setUser(session.user);

        // Defer async admin verification to prevent blocking auth flow
        setTimeout(async () => {
          if (!isMounted) return;
          try {
            await verifyAdminUser(session.user.id);
          } catch (error) {
            console.error('[AUTH] Failed to verify admin user:', error);
            // Don't clear session on verification error - user might still be valid
          } finally {
            if (isMounted) {
              setIsLoading(false);
            }
          }
        }, 0);
      } else if (event === 'TOKEN_REFRESHED' && session) {
        // Token refresh - only update session, NO admin verification needed
        console.log('[AUTH] Token refreshed, updating session silently');
        setSession(session);
        // Don't refetch admin profile or user - just update token
      } else if (event === 'SIGNED_OUT') {
        // Sign out - clear all state
        console.log('[AUTH] User signed out');
        setSession(null);
        setUser(null);
        setAdminUser(null);
        setIsLoading(false);
      } else if (event === 'USER_UPDATED' && session) {
        // User metadata updated
        console.log('[AUTH] User updated');
        setSession(session);
        setUser(session.user);
      } else {
        // Other events (INITIAL_SESSION handled in initializeSession)
        console.log('[AUTH] Other event:', event);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [verifyAdminUser]);

  /**
   * Keeps a stable reference to the latest admin user for callbacks.
   */
  useEffect(() => {
    adminUserRef.current = adminUser;
  }, [adminUser]);

  /**
   * Track current user ID for re-authentication detection.
   */
  useEffect(() => {
    currentUserIdRef.current = user?.id || null;
  }, [user?.id]);

  /**
   * Smart visibility handler - validates session when tab becomes visible.
   * Only runs when:
   * - Tab becomes visible (not hidden)
   * - User is authenticated
   * - Session exists
   * - Not currently signing in
   *
   * This prevents unnecessary refetches and only does a lightweight session check.
   */
  useEffect(() => {
    const handleVisibilityChange = async () => {
      // Only validate if ALL these conditions are true
      const shouldValidate =
        !document.hidden &&        // Tab is visible
        user?.id &&                // User is authenticated
        session &&                 // Session exists
        !isSigningIn &&            // Not during sign-in
        !isLoading;                // Not during initial load

      if (!shouldValidate) {
        return;
      }

      console.log('[VISIBILITY] Tab visible, validating session');

      try {
        // Lightweight session check (no data fetch)
        const { data: { session: currentSession }, error } =
          await supabase.auth.getSession();

        if (error) {
          // Only sign out on actual session errors
          if (error.message.includes('invalid') ||
              error.message.includes('expired')) {
            console.log('[VISIBILITY] Session invalid, signing out');
            // Log logout event
            const currentAdmin = adminUserRef.current;
            if (currentAdmin) {
              await safeLogAuthEvent(currentAdmin.id, currentAdmin.email, AuditActionTypes.LOGOUT);
            }
            await supabase.auth.signOut();
            setAdminUser(null);
            setUser(null);
            setSession(null);
          }
          // Ignore network errors - don't sign out
        } else if (currentSession) {
          // Valid session - update silently
          console.log('[VISIBILITY] Session valid, updating silently');
          setSession(currentSession);
        } else {
          // No session - sign out
          console.log('[VISIBILITY] No session found, signing out');
          // Log logout event
          const currentAdmin = adminUserRef.current;
          if (currentAdmin) {
            await safeLogAuthEvent(currentAdmin.id, currentAdmin.email, AuditActionTypes.LOGOUT);
          }
          await supabase.auth.signOut();
          setAdminUser(null);
          setUser(null);
          setSession(null);
        }
      } catch (error) {
        console.error('[VISIBILITY] Session check failed:', error);
        // Don't sign out on exceptions (could be network issue)
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id, session, isSigningIn, isLoading, safeLogAuthEvent]);

  /**
   * Sign in with email and password.
   * 
   * SECURITY: All authentication events (success and failure) are logged
   * to admin_audit_logs for compliance and security monitoring.
   * 
   * @see CSDEV-139 - Audit logging for login/logout/failed login
   */
  const signIn = useCallback(
    async (email: string, password: string): Promise<{ error?: string }> => {
      setIsLoading(true);
      setIsSigningIn(true);

      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          setIsLoading(false);
          setIsSigningIn(false);
          // Log failed login attempt
          await safeLogAuthEvent(null, email, AuditActionTypes.LOGIN_FAILED, {
            reason: error.message,
          });
          return { error: error.message };
        }

        if (!data.user) {
          setIsLoading(false);
          setIsSigningIn(false);
          // Log failed login attempt
          await safeLogAuthEvent(null, email, AuditActionTypes.LOGIN_FAILED, {
            reason: 'No user returned from auth',
          });
          return { error: 'Sign in failed. Please try again.' };
        }

        // Verify admin status
        const admin = await verifyAdminUser(data.user.id);
        setIsLoading(false);
        setIsSigningIn(false);

        if (!admin) {
          // Log failed login attempt (not an admin)
          await safeLogAuthEvent(null, email, AuditActionTypes.LOGIN_FAILED, {
            reason: 'User is not an admin',
          });
          // Sign out if not an admin
          await supabase.auth.signOut();
          return { error: 'Access denied. You are not authorized to access this dashboard.' };
        }

        // Log successful login
        await safeLogAuthEvent(admin.id, admin.email, AuditActionTypes.LOGIN);

        return {};
      } catch (err) {
        setIsLoading(false);
        setIsSigningIn(false);
        console.error('Sign in error:', err);
        // Log unexpected error
        await safeLogAuthEvent(null, email, AuditActionTypes.LOGIN_FAILED, {
          reason: 'Unexpected error',
          error: err instanceof Error ? err.message : 'Unknown error',
        });
        return { error: 'An unexpected error occurred. Please try again.' };
      }
    },
    [safeLogAuthEvent, verifyAdminUser]
  );

  /**
   * Sign out and clear session.
   * 
   * SECURITY: Logout events are logged to admin_audit_logs
   * for compliance and security monitoring.
   * 
   * @see CSDEV-139 - Audit logging for login/logout/failed login
   */
  const signOut = useCallback(async () => {
    // Log logout event before clearing state
    const currentAdmin = adminUserRef.current;
    if (currentAdmin) {
      await safeLogAuthEvent(currentAdmin.id, currentAdmin.email, AuditActionTypes.LOGOUT);
    }

    await supabase.auth.signOut();
    setAdminUser(null);
    setUser(null);
    setSession(null);
  }, [safeLogAuthEvent]);

  return (
    <AuthContext.Provider
      value={{
        user,
        adminUser,
        session,
        isLoading,
        isAdmin: !!adminUser,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access authentication context.
 * 
 * @returns Authentication context
 * @throws Error if used outside AuthProvider
 * 
 * @example
 * const { adminUser, signOut, isAdmin } = useAuth();
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
